export const STAFF_REPORT_ROLES = {
  1: ["Civil Engineer", "Project Manager", "Supervisor", "Foreman"],
  2: ["Office Staff", "Personal Assistant"],
  3: ["Tele Calling"],
  4: ["Real Estate Sales", "Manager Real Estate"],
  5: ["Auditor", "Accountant"],
  6: ["Revenue"],
  7: ["Manager"],
  8: ["Manager Netplus"],
};

export const FORM_TITLES = {
  1: "Field Execution Journal",
  2: "Office Monthly Register",
  3: "Tele Calling Log",
  4: "Sales Activity Log",
  5: "Audit & Accounting Sheet",
  6: "Revenue Work Sheet",
  7: "Netplus Updates",
  8: "Netplus Manager Daily",
};

export const REPORT_STATUS_OPTIONS = {
  1: ["Pending", "In Progress", "Complete"],
  2: ["Pending", "In Progress", "Complete"],
  3: ["Pending", "In Progress", "Complete"],
  4: ["Pending", "In Progress", "Complete"],
  5: ["Pending", "Completed", "Under Review"],
  6: ["Pending", "In Progress", "Completed", "Under Review"],
  7: ["Pending", "In Progress", "Complete"],
  8: ["Pending", "In Progress", "Complete"],
};

// ─── Netplus Updates (formType 7) — Manager daily area status counts ─────────
// Three fixed areas (by code) × five connection statuses, one count per status
// per day. Areas + statuses are fixed so every Manager's sheet reads the same.
export const NETPLUS_AREA_CODES = ["204", "240", "241"];

export const NETPLUS_STATUS_FIELDS = [
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "suspended", label: "Suspended" },
  { key: "terminated", label: "Terminated" },
  { key: "unidentified", label: "Unidentified" },
];

// ─── Netplus Manager Daily (formType 8) — Manager Netplus's own daily sheet ──
// One row per day, typed in by the manager. Recovered / recharged / untraceable
// / visits accumulate (running total shown); pending cases and balance are the
// end-of-day position.
export const NETPLUS_MGR_FIELDS = [
  { key: "pendingCases", label: "Total Pending Cases", cumulative: false },
  { key: "todayVisits", label: "Today Visits", cumulative: true },
  { key: "modemRecovered", label: "Modem Recovered", cumulative: true },
  { key: "recharged", label: "Recharged", cumulative: true },
  { key: "untraceable", label: "Untraceable", cumulative: true },
  { key: "balance", label: "Balance", cumulative: false },
];

export const getNetplusMgrEntry = (entries, date) => {
  const row = { date };
  const found = (Array.isArray(entries) ? entries : []).find(
    (e) => e?.date === date,
  );
  NETPLUS_MGR_FIELDS.forEach(({ key }) => {
    row[key] = found?.[key] ?? "";
  });
  return row;
};

export const upsertNetplusMgrEntry = (entries, date, patch) => {
  const list = Array.isArray(entries) ? entries : [];
  const next = { ...getNetplusMgrEntry(list, date), ...patch, date };
  return [...list.filter((e) => e?.date !== date), next].sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  );
};

// Running totals (cumulative fields only) up to and including each row.
export const withNetplusMgrRunningTotals = (entries) => {
  const run = {};
  NETPLUS_MGR_FIELDS.filter((f) => f.cumulative).forEach(({ key }) => {
    run[key] = 0;
  });
  return [...(Array.isArray(entries) ? entries : [])]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((e) => {
      Object.keys(run).forEach((k) => {
        run[k] += Number(e[k]) || 0;
      });
      return { ...e, running: { ...run } };
    });
};

export const createNetplusAreas = () =>
  NETPLUS_AREA_CODES.map((code) => ({ code, entries: [] }));

const emptyNetplusEntry = (date) => {
  const row = { date };
  NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
    row[key] = "";
  });
  return row;
};

// The counts logged for one area on one date, or a blank row if nothing yet.
export const getNetplusEntry = (area, date) => {
  const entries = Array.isArray(area?.entries) ? area.entries : [];
  return entries.find((entry) => entry?.date === date) || emptyNetplusEntry(date);
};

// Upsert one area's entry for `date` — only that date's row changes, the rest
// of the month's history is preserved by the backend merge.
export const upsertNetplusEntry = (areas, date, code, valuesByKey) =>
  (Array.isArray(areas) ? areas : []).map((area) => {
    if (String(area.code) !== String(code)) return area;
    const entries = Array.isArray(area.entries) ? area.entries : [];
    const withoutDate = entries.filter((entry) => entry?.date !== date);
    const next = { date };
    NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
      next[key] = valuesByKey[key] ?? "";
    });
    return {
      ...area,
      entries: [...withoutDate, next].sort((a, b) =>
        String(a.date).localeCompare(String(b.date)),
      ),
    };
  });

// Column totals for one area across the whole month — handy in the detail view.
export const sumNetplusArea = (area) => {
  const totals = {};
  NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
    totals[key] = 0;
  });
  (Array.isArray(area?.entries) ? area.entries : []).forEach((entry) => {
    NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
      totals[key] += Number(entry?.[key]) || 0;
    });
  });
  return totals;
};

export const TECHNICAL_ROLE_OPTIONS = [
  "Project Manager",
  "Supervisor",
  "Civil Engineer",
];

// Fixed metric rows per role-wise sheet (role-wise-sheet.xlsx). Each metric is
// tracked as one value per date, matching the sheet's rows-as-metrics /
// columns-as-dates layout.
export const FORM_METRICS = {
  2: [
    { key: "deactiveCall", label: "Deactive Call" },
    { key: "recharge", label: "Recharge" },
    { key: "complaints", label: "Complaints" },
    { key: "complaintsResolved", label: "Complaints Resolved" },
    { key: "complaintsPending", label: "Complaints Pending" },
    { key: "callingOffline", label: "Calling Offline" },
    { key: "callingWhatsapp", label: "Calling Whatsapp" },
    { key: "otherWork", label: "Other Work" },
  ],
  3: [
    { key: "callingNetplus", label: "Netplus Calling" },
    { key: "callingOnlineLeads", label: "New Leads Calling" },
    { key: "callingHotelFeedback", label: "Hotel Calling" },
    { key: "callingCp", label: "CP Calling" },
    { key: "callingInterestedClients", label: "Client Calling" },
    { key: "visitClient", label: "Visit Client" },
    // Free-text remarks for the day, not a running count.
    { key: "dataMaintained", label: "Data Maintained", text: true },
    { key: "extraWork", label: "Extra Work", text: true },
  ],
  4: [
    { key: "amohaSiteVisit", label: "Amoha Site Visit" },
    { key: "openVisit", label: "Open Visit" },
  ],
};

export const formatRoleLabel = (role) => String(role || "").replace(/-/g, " ");

export const formatDateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// "Sep-2026" style month label — used wherever a date only needs to read as
// the month it falls in (the Customer Database's Month column), not the day.
export const formatMonthLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${SHORT_MONTHS[date.getMonth()]}-${date.getFullYear()}`;
};

export const toDateInputValue = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toMonthKey = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
    return toDateInputValue(new Date()).slice(0, 7);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const getFormTypeForRole = (role) => {
  const normalized = String(role || "");
  return Number(
    Object.entries(STAFF_REPORT_ROLES).find(([, roles]) =>
      roles.includes(normalized),
    )?.[0] || 0,
  );
};

export const createSubWork = (dateValue = toDateInputValue()) => ({
  subWorkName: "",
  workStartDate: dateValue,
  workExtendDate: dateValue,
  status: "Pending",
  // Dated history of follow-up notes on this sub-work specifically — same
  // append-only shape as a Revenue task's updatedRemarks.
  updatedRemarks: [],
});

// ─── Field Execution Journal (formType 1) — Unit → Main Works → Sub Works ─────
// A sheet is now keyed to a single Unit (e.g. "Villa 12") and holds every
// main work for that unit, each with its own nested sub-works. IDs are stable
// per row so the backend can deep-merge concurrent/daily edits by id instead
// of replacing the whole array.

export const newRowId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const FIELD_STATUS_OPTIONS = ["Pending", "In Progress", "Complete"];

// Admin sign-off on a completed main / sub work: `verified` is the checkbox the
// admin ticks, `remarks` their note; the name + date are stamped on submit.
export const createWorkVerification = () => ({
  verified: false,
  remarks: "",
  verifiedByName: "",
  verifiedById: "",
  photoUrl: "",
  verifiedAt: "",
});

export const normalizeWorkVerification = (value) => {
  if (!value || typeof value !== "object") return null;
  return {
    verified: Boolean(value.verified),
    remarks: String(value.remarks || ""),
    verifiedByName: String(value.verifiedByName || ""),
    // Who signed off + their site photo — read by the Performance Review, so
    // they must survive a re-save of the sheet.
    verifiedById: String(value.verifiedById || ""),
    photoUrl: String(value.photoUrl || ""),
    verifiedAt: value.verifiedAt || "",
  };
};

export const isWorkVerified = (work) =>
  Boolean(work && work.verification && work.verification.verified);

// One admin deadline-extension: the new target it pushed the work out to, why,
// whether it was the employee's fault, and who did it / when. `extensions` on a
// work is an append-only list of these — `extensions.length` is "how many times
// this work's deadline has been extended".
export const normalizeExtensions = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      return {
        date: entry.date || "",
        reason: String(entry.reason || "").trim(),
        dueToEmployee: Boolean(entry.dueToEmployee),
        extendedByName: String(entry.extendedByName || ""),
        extendedAt: entry.extendedAt || "",
      };
    })
    .filter((entry) => entry && (entry.date || entry.reason));

// A work's hold/resume trail — one entry per hold, closed out by the matching
// resume (blank resumeDate/newCompletionDate = still on hold).
export const normalizeHoldHistory = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      return {
        holdDate: entry.holdDate || "",
        resumeDate: entry.resumeDate || "",
        reason: String(entry.reason || "").trim(),
        heldByName: String(entry.heldByName || ""),
        previousCompletionDate: entry.previousCompletionDate || "",
        newCompletionDate: entry.newCompletionDate || "",
      };
    })
    .filter(Boolean);

// True while a main/sub work is paused — its deadline countdown is frozen and
// it's excluded from overdue/performance reckoning until resumed.
export const isWorkOnHold = (work) => Boolean(work && work.isOnHold);

export const createFieldSubWork = (dateValue = toDateInputValue()) => ({
  id: newRowId("sw"),
  subWorkName: "",
  startDate: dateValue,
  // The ORIGINAL deadline. Once set it is never overwritten by an extension —
  // an admin pushes the target out via `extendedDate` instead, so the delay
  // can always be measured against the date the work was first promised.
  completionDate: "",
  // Admin-set revised target when the work slips past `completionDate`. Blank
  // = never extended. Always >= completionDate.
  extendedDate: "",
  // Reason for the MOST RECENT extension + whether it was the employee's fault.
  // The full trail is in `extensions` (append-only) — this pair is the latest.
  extensionReason: "",
  extendedDueToEmployee: false,
  extensions: [],
  status: "Pending",
  remarks: "",
  // The day the work was actually finished — stamped from the update form's
  // Date field the moment status becomes Complete, cleared if it moves back.
  // This, not "today", is what "completed N days late" / "on time" measures.
  completedOn: "",
  // Append-only daily-work log — each update pushes { date, text }. Rendered
  // newest-first (see groupRemarksByDate's newestFirst option).
  updatedRemarks: [],
  // Admin verification of the finished work — null until an admin signs off.
  verification: null,
  // Self-service pause: the employee holds a work item with a reason, the
  // deadline countdown freezes, and resuming sets a fresh completionDate.
  isOnHold: false,
  holdReason: "",
  holdDate: "",
  holdHistory: [],
});

export const createMainWork = (dateValue = toDateInputValue()) => ({
  id: newRowId("mw"),
  mainWorkName: "",
  startDate: dateValue,
  completionDate: "",
  extendedDate: "",
  extensionReason: "",
  extendedDueToEmployee: false,
  extensions: [],
  isOnHold: false,
  holdReason: "",
  holdDate: "",
  holdHistory: [],
  status: "Pending",
  completedOn: "",
  remarks: "",
  // Dated update log for a main work that has no sub-works of its own.
  updatedRemarks: [],
  subWorks: [createFieldSubWork(dateValue)],
  verification: null,
});

// One open sheet per (employee, unit): the unit name — trimmed, lower-cased,
// whitespace-collapsed — is the formType-1 periodKey.
export const normalizeUnitKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const createMetricSet = (formType) => {
  const definitions = FORM_METRICS[Number(formType)] || [];
  return definitions.map(({ key, label, text }) => ({
    key,
    label,
    entries: [],
    ...(text ? { text: true } : {}),
  }));
};

// Keys that hold free-text remarks (per day) rather than a running number.
export const isTextMetricKey = (formType, key) =>
  (FORM_METRICS[Number(formType)] || []).some(
    (m) => m.key === key && m.text,
  );

export const OPEN_VISIT_KEY = "openVisit";

export const buildSalesMetricKey = (projectId) => `siteVisit-${projectId}`;

// Real Estate Sales (formType 4) metric rows are project-aware: one "<Project>
// Site Visit" row per project in the system, plus a single project-independent
// "Open Visit" row that always appears exactly once regardless of project
// count. Falls back to the static FORM_METRICS[4] rows (a single generic
// "Amoha Site Visit" + "Open Visit") until the project list has loaded, so
// pre-existing saved entries are never dropped while projects are in flight.
export const buildSalesMetrics = (projects, existingMetrics) => {
  const existing = Array.isArray(existingMetrics) ? existingMetrics : [];
  const projectList = Array.isArray(projects) ? projects : [];

  if (!projectList.length) {
    return existing.length ? existing : createMetricSet(4);
  }

  const entriesByKey = new Map(existing.map((metric) => [metric.key, metric.entries]));

  const projectRows = projectList.map((project) => {
    const projectId = String(project._id ?? project.id ?? "");
    const key = buildSalesMetricKey(projectId);
    return {
      key,
      label: `${project.projectName ?? project.name ?? "Project"} Site Visit`,
      projectId,
      entries: entriesByKey.get(key) || [],
    };
  });

  return [
    ...projectRows,
    {
      key: OPEN_VISIT_KEY,
      label: "Open Visit",
      entries: entriesByKey.get(OPEN_VISIT_KEY) || [],
    },
  ];
};

// India-time calendar day ("YYYY-MM-DD") of a timestamp — the server's
// `savedAt` stamp on each dated entry is compared with the entry's own day.
export const istDayKey = (value) =>
  new Date(new Date(value).getTime() + 330 * 60 * 1000).toISOString().slice(0, 10);

export const getMetricEntryValue = (metric, dateValue) => {
  const entries = Array.isArray(metric?.entries) ? metric.entries : [];
  return (
    entries.find((entry) => entry?.date === dateValue)?.value ?? ""
  );
};

// Upserts today's (dateValue) value for every metric in one pass — used on
// submit so only the selected date's entry changes, the rest of the month's
// history is preserved by the backend merge.
export const upsertMetricEntries = (metrics, dateValue, valuesByKey) => {
  return (Array.isArray(metrics) ? metrics : []).map((metric) => {
    const nextValue = valuesByKey[metric.key];
    if (nextValue === undefined) return metric;
    const entries = Array.isArray(metric.entries) ? metric.entries : [];
    const withoutDate = entries.filter((entry) => entry?.date !== dateValue);
    return {
      ...metric,
      entries: [...withoutDate, { date: dateValue, value: nextValue }].sort(
        (a, b) => String(a.date).localeCompare(String(b.date)),
      ),
    };
  });
};

// Real Estate Sales (formType 4) — one row per customer/dealer interaction
// logged against a date. Every text field is optional; a row with only a date
// is still valid (and is dropped on save if left completely blank).
export const createCustomerDealerEntry = (dateValue = toDateInputValue()) => ({
  date: dateValue,
  customerName: "",
  customerPhone: "",
  dealerName: "",
  dealerPhone: "",
});

export const isCustomerDealerEntryEmpty = (entry) =>
  !["customerName", "customerPhone", "dealerName", "dealerPhone"].some((key) =>
    String(entry?.[key] || "").trim(),
  );

// ─── Tele Calling (3) & Real Estate Sales (4) — contact + call-log model ─────
// One monthly sheet per employee. `contacts` holds the data rows the caller /
// salesperson adds; each contact carries an append-only `calls` log (the
// "tick Called → type remark → save" flow). Real Estate Sales additionally
// keeps `dailyStats` — a manual Visits / Sales count per day — and both roles
// carry `openingTotals`, the running balance rolled forward from last month.
export const CONTACT_REPORT_FORMS = [3, 4];

// Lead source, property type & budget band — captured once per customer
// alongside the remarks / call-log fields.
export const LEAD_SOURCE_OPTIONS = ["Social Media", "Walk In"];
export const PROPERTY_TYPE_OPTIONS = [
  "Flat",
  "Villa",
  "Kothi",
  "Plot",
  "Commercial",
];
export const BUDGET_RANGE_OPTIONS = [
  "1-50 L",
  "51 L-1 Cr",
  "1-1.5 Cr",
  "1.5 Cr+",
];

export const createContact = (dateValue = toDateInputValue()) => ({
  id: newRowId("ct"),
  createdDate: dateValue,
  customerName: "",
  customerPhone: "",
  remarks: "",
  lead: "",
  propertyType: "",
  budget: "",
  // [{ date: "YYYY-MM-DD", at: ISO string, remarks }] — append-only.
  calls: [],
});

export const isContactEmpty = (contact) =>
  !["customerName", "customerPhone", "remarks", "lead", "propertyType", "budget"].some(
    (key) => String(contact?.[key] || "").trim(),
  ) && !(Array.isArray(contact?.calls) && contact.calls.length);

// ─── Master Customer roster (persistent, one per employee per role) ──────────
// { id, name, phone, createdDate, archived,
//   dailyRemarks: [{ date, remarks, reportId }], remarkCount }
// Identity fields (name/phone) stay editable for the life of the record,
// remarks or not.
export const createMasterCustomer = (dateValue = toDateInputValue()) => ({
  id: newRowId("cust"),
  name: "",
  phone: "",
  lead: "",
  propertyType: "",
  budget: "",
  createdDate: dateValue,
  archived: false,
  dailyRemarks: [],
  remarkCount: 0,
});

// Name/phone used to freeze after the first remark; unlocked by request —
// always editable now, remarks or not.
export const isMasterCustomerLocked = () => false;

// Build a period report's `contacts` array from the master roster: one row per
// non-archived master customer (reusing the same id), carrying over any calls
// already logged on this report, plus any report contacts not (yet) in master.
export const mergeMasterIntoContacts = (
  masterCustomers,
  reportContacts,
  dateValue = toDateInputValue(),
) => {
  const master = Array.isArray(masterCustomers) ? masterCustomers : [];
  const existing = Array.isArray(reportContacts) ? reportContacts : [];
  const existingById = new Map(existing.map((c) => [String(c.id), c]));
  const masterIds = new Set(master.map((c) => String(c.id)));

  const fromMaster = master
    .filter((c) => !c.archived)
    .map((c) => {
      const prev = existingById.get(String(c.id));
      return {
        id: c.id,
        createdDate: c.createdDate || dateValue,
        customerName: c.name || "",
        customerPhone: c.phone || "",
        remarks: prev?.remarks || "",
        lead: prev?.lead || c.lead || "",
        propertyType: prev?.propertyType || c.propertyType || "",
        budget: prev?.budget || c.budget || "",
        calls: Array.isArray(prev?.calls) ? prev.calls : [],
        // history from the roster, for display only
        dailyRemarks: Array.isArray(c.dailyRemarks) ? c.dailyRemarks : [],
        fromMaster: true,
      };
    });

  // Report contacts added on the report but not synced to master yet.
  const orphans = existing.filter((c) => !masterIds.has(String(c.id)));

  return [...fromMaster, ...orphans];
};

// Append one call to a contact's log — used by the "Called" tick flow.
export const appendContactCall = (contacts, contactId, remarks, when = new Date()) =>
  (Array.isArray(contacts) ? contacts : []).map((contact) =>
    contact.id === contactId
      ? {
          ...contact,
          calls: [
            ...(Array.isArray(contact.calls) ? contact.calls : []),
            {
              date: toDateInputValue(when),
              at: new Date(when).toISOString(),
              remarks: String(remarks || "").trim(),
            },
          ],
        }
      : contact,
  );

// Upsert one day's { openVisits, siteVisits, calls, sales } row in dailyStats
// (only that date changes; the rest of the month's history is untouched).
export const upsertDailyStat = (rows, date, patch) => {
  const list = Array.isArray(rows) ? rows : [];
  const existing = list.find((row) => row?.date === date) || {
    date,
    openVisits: 0,
    siteVisits: 0,
    calls: 0,
    sales: 0,
  };
  const next = { ...existing, ...patch, date };
  return [...list.filter((row) => row?.date !== date), next].sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  );
};

// Manual daily-activity counts, one row per day.
export const DAILY_STAT_KEYS = ["openVisits", "siteVisits", "calls", "sales"];

export const zeroDailyTotals = () => ({
  openVisits: 0,
  siteVisits: 0,
  calls: 0,
  sales: 0,
});

export const getDailyStat = (rows, date) => {
  const found = (Array.isArray(rows) ? rows : []).find(
    (row) => row?.date === date,
  );
  if (!found)
    return { date, openVisits: "", siteVisits: "", calls: "", sales: "", remarks: "" };
  // fold a pre-change `visits` field into openVisits
  return {
    date,
    openVisits: statText(found.openVisits) || statText(found.visits),
    siteVisits: statText(found.siteVisits),
    calls: statText(found.calls),
    sales: statText(found.sales),
    remarks: statText(found.remarks),
  };
};

// Daily activity values are free text, e.g. "5 - Ramesh, Suresh". Totals count
// the number at the start (5); text without one counts 0. Same rule as the
// backend's utils/dailyStatValue.js.
export const statNum = (value) => {
  if (typeof value === "number")
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
  const m = String(value ?? "").match(/^\s*(\d+)/);
  return m ? Number(m[1]) : 0;
};
const toInt = statNum;

// The text for an input, untrimmed so typing a space isn't eaten; an old
// numeric 0 shows as blank. The server trims on save.
export const statText = (value) =>
  value === null || value === undefined || value === 0 ? "" : String(value);

// Detail page "add to the day": counts add up and the notes are joined, so
// "2 - Ramesh" + "1 - Suresh" -> "3 - Ramesh, Suresh".
export const addDailyStatText = (existing, typed) => {
  const notesOf = (v) =>
    statText(v).replace(/^\s*\d+/, "").replace(/^[\s\-:,;]+/, "").trim();
  const prev = statText(existing).trim();
  const next = statText(typed).trim();
  if (!prev) return next;
  if (!next) return prev;
  const total = statNum(prev) + statNum(next);
  const notes = [notesOf(prev), notesOf(next)].filter(Boolean).join(", ");
  if (!/^\s*\d/.test(prev) && !/^\s*\d/.test(next)) return notes;
  return notes ? `${total} - ${notes}` : String(total);
};

// Live tile numbers for a formType 3/4 sheet. Mirrors the backend's
// deriveReportingSummary so the Detail view can recompute as the user edits.
// "Calls" = manual daily calls + per-contact logged calls (the Called flow).
export const deriveReportingSummary = (
  formData = {},
  today = toDateInputValue(),
) => {
  const contacts = Array.isArray(formData.contacts) ? formData.contacts : [];
  const stats = Array.isArray(formData.dailyStats) ? formData.dailyStats : [];
  const opening = { ...zeroDailyTotals(), ...(formData.openingTotals || {}) };
  const allCalls = contacts.flatMap((c) =>
    Array.isArray(c.calls) ? c.calls : [],
  );
  const loggedCallsToday = allCalls.filter((c) => c.date === today).length;
  const loggedCallsMonth = allCalls.length;
  const monthOf = (key) =>
    stats.reduce(
      (sum, r) =>
        sum +
        (key === "openVisits" && !r?.openVisits
          ? toInt(r?.visits)
          : toInt(r?.[key])),
      0,
    );
  const todayRow = getDailyStat(stats, today);
  const line = (key, loggedToday = 0, loggedMonth = 0) => {
    const month = monthOf(key);
    return {
      today: toInt(todayRow[key]) + loggedToday,
      month: month + loggedMonth,
      running: toInt(opening[key]) + month + loggedMonth,
    };
  };
  return {
    today,
    contacts: contacts.length,
    openVisits: line("openVisits"),
    siteVisits: line("siteVisits"),
    calls: line("calls", loggedCallsToday, loggedCallsMonth),
    sales: line("sales"),
  };
};

export const createAccountantTask = (dateValue = toDateInputValue()) => ({
  assignDate: dateValue,
  work: "",
  dueDate: dateValue,
  status: "Pending",
  remarks: "",
});

export const createRevenueTask = (dateValue = toDateInputValue()) => ({
  workAssignDate: dateValue,
  work: "",
  workDueDate: dateValue,
  status: "Pending",
  remarks: "",
  // Dated history of follow-up notes — each edit appends a new
  // { date, text } entry instead of overwriting the previous one.
  updatedRemarks: [],
  assignedTechnicalStaff: "",
  technicalStaffEnabled: true,
  // Nested sub-works under this main work, same shape as Field Execution
  // Journal's subWorks (createSubWork).
  subWorks: [],
});

// Accepts either the current [{ date, text }] shape or a legacy plain string
// (from records saved before dated history existed) and always returns an
// array, so callers never have to branch on the old format.
export const normalizeRemarksHistory = (value, fallbackDate = toDateInputValue()) => {
  if (Array.isArray(value)) {
    return value.filter((entry) => entry && String(entry.text || "").trim());
  }
  if (typeof value === "string" && value.trim()) {
    return [{ date: fallbackDate, text: value.trim() }];
  }
  return [];
};

// Groups a [{ date, text }] history into one entry per date (in the order
// each date first appears — the history is already append-only/chronological)
// so callers can render one row per date instead of one row per note.
// Pass { newestFirst: true } to reverse the groups so the latest update for a
// sub-work renders above older ones.
export const groupRemarksByDate = (history, { newestFirst = false } = {}) => {
  const order = [];
  const textsByDate = new Map();
  normalizeRemarksHistory(history).forEach((entry) => {
    const date = entry.date || "";
    if (!textsByDate.has(date)) {
      textsByDate.set(date, []);
      order.push(date);
    }
    textsByDate.get(date).push(entry.text);
  });
  const groups = order.map((date) => ({ date, texts: textsByDate.get(date) }));
  return newestFirst ? groups.reverse() : groups;
};

// Old formType-1 sheets stored a single main work as top-level
// A formType-1 sub-work "daily update" entry: a dated progress note plus the
// head-count that worked on it that day. Accepts the legacy [{ date, text }]
// shape (peopleCount defaults blank) and plain strings.
export const normalizeSubWorkUpdates = (
  value,
  fallbackDate = toDateInputValue(),
) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((entry) => {
      if (typeof entry === "string") {
        return { date: fallbackDate, text: entry.trim(), peopleCount: "" };
      }
      const raw = entry?.peopleCount;
      return {
        date: entry?.date || fallbackDate,
        text: String(entry?.text || "").trim(),
        peopleCount:
          raw === "" || raw === null || raw === undefined
            ? ""
            : Number(raw) || 0,
      };
    })
    .filter((entry) => entry.text || entry.peopleCount !== "");
};

// Chronological (newest-first by default) list of a sub-work's daily updates,
// each { date, text, peopleCount } — one row per update, not grouped by date.
export const sortSubWorkUpdates = (history, { newestFirst = true } = {}) => {
  const sorted = normalizeSubWorkUpdates(history).sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  );
  return newestFirst ? sorted.reverse() : sorted;
};

// { work, workAssignDate, subWorks:[{ subWorkName, workStartDate,
// workExtendDate, status, updatedRemarks }] }. New sheets store
// { unitName, mainWorks:[{ ...id, subWorks:[{ ...id }] }] }. This folds the
// legacy shape into the new one at read time so the editor never has to branch
// on format — nothing is written back unless the user saves.
export const normalizeFieldReport = (formData = {}) => {
  const data = formData && typeof formData === "object" ? formData : {};

  if (Array.isArray(data.mainWorks) && data.mainWorks.length) {
    return {
      ...data,
      unitName: data.unitName || "",
      mainWorks: data.mainWorks.map((mainWork) => ({
        id: mainWork.id || newRowId("mw"),
        mainWorkName: mainWork.mainWorkName || "",
        startDate: mainWork.startDate || "",
        completionDate: mainWork.completionDate || "",
        extendedDate: mainWork.extendedDate || "",
        extensionReason: mainWork.extensionReason || "",
        extendedDueToEmployee: Boolean(mainWork.extendedDueToEmployee),
        extensions: normalizeExtensions(mainWork.extensions),
        isOnHold: Boolean(mainWork.isOnHold),
        holdReason: mainWork.holdReason || "",
        holdDate: mainWork.holdDate || "",
        holdHistory: normalizeHoldHistory(mainWork.holdHistory),
        status: mainWork.status || "Pending",
        completedOn: mainWork.completedOn || "",
        remarks: mainWork.remarks || "",
        // Kept so a main work with no sub-works can still carry its own dated
        // update log (same append-only shape as a sub-work's).
        updatedRemarks: normalizeSubWorkUpdates(mainWork.updatedRemarks),
        verification: normalizeWorkVerification(mainWork.verification),
        subWorks: (Array.isArray(mainWork.subWorks) ? mainWork.subWorks : []).map(
          (subWork) => ({
            id: subWork.id || newRowId("sw"),
            subWorkName: subWork.subWorkName || "",
            startDate: subWork.startDate || subWork.workStartDate || "",
            completionDate:
              subWork.completionDate || subWork.workExtendDate || "",
            extendedDate: subWork.extendedDate || "",
            extensionReason: subWork.extensionReason || "",
            extendedDueToEmployee: Boolean(subWork.extendedDueToEmployee),
            extensions: normalizeExtensions(subWork.extensions),
            isOnHold: Boolean(subWork.isOnHold),
            holdReason: subWork.holdReason || "",
            holdDate: subWork.holdDate || "",
            holdHistory: normalizeHoldHistory(subWork.holdHistory),
            status: subWork.status || "Pending",
            completedOn: subWork.completedOn || "",
            remarks: subWork.remarks || "",
            updatedRemarks: normalizeSubWorkUpdates(subWork.updatedRemarks),
            verification: normalizeWorkVerification(subWork.verification),
          }),
        ),
      })),
    };
  }

  const legacySubWorks = Array.isArray(data.subWorks) ? data.subWorks : [];

  // `toLegacyFieldMirror` joins every main work name into `data.work`
  // ("A, B, C") and prefixes each flat sub-work as "Main — Sub". Rebuild the
  // individual main works from that so the editor offers one option per work
  // instead of a single giant comma-joined option.
  const mainNames = [
    ...new Set(
      String(data.work || "")
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean),
    ),
  ];
  const effectiveNames = mainNames.length ? mainNames : ["General Work"];

  const stripMainPrefix = (subName) => {
    const raw = String(subName || "");
    const lower = raw.toLowerCase();
    const match = [...effectiveNames]
      .sort((a, b) => b.length - a.length)
      .find((name) =>
        [" — ", " – ", " - "].some((sep) =>
          lower.startsWith(`${name.toLowerCase()}${sep}`),
        ),
      );
    if (!match) return { mainName: null, subName: raw };
    return {
      mainName: match,
      subName: raw.slice(match.length).replace(/^\s*[—–-]\s*/, "").trim(),
    };
  };

  const buckets = new Map(effectiveNames.map((name) => [name, []]));
  legacySubWorks.forEach((subWork) => {
    const { mainName, subName } = stripMainPrefix(subWork.subWorkName);
    const key =
      mainName && buckets.has(mainName) ? mainName : effectiveNames[0];
    buckets.get(key).push({
      id: subWork.id || newRowId("sw"),
      subWorkName: subName || subWork.subWorkName || "",
      startDate: subWork.workStartDate || subWork.startDate || "",
      completionDate: subWork.workExtendDate || subWork.completionDate || "",
      extendedDate: subWork.extendedDate || "",
      extensionReason: subWork.extensionReason || "",
      extendedDueToEmployee: Boolean(subWork.extendedDueToEmployee),
      extensions: normalizeExtensions(subWork.extensions),
      status: subWork.status || "Pending",
      completedOn: subWork.completedOn || "",
      remarks: "",
      updatedRemarks: normalizeSubWorkUpdates(subWork.updatedRemarks),
      verification: normalizeWorkVerification(subWork.verification),
    });
  });

  return {
    ...data,
    unitName: data.unitName || "",
    mainWorks: effectiveNames.map((name) => ({
      id: newRowId("mw"),
      mainWorkName: name,
      startDate: buckets.get(name)[0]?.startDate || data.workAssignDate || "",
      completionDate: "",
      extendedDate: "",
      extensionReason: "",
      extendedDueToEmployee: false,
      extensions: [],
      status: "Pending",
      completedOn: "",
      subWorks: buckets.get(name),
      verification: null,
    })),
  };
};

// Flat legacy mirror written alongside mainWorks on every save so the
// not-yet-migrated Detail/List/Excel views keep rendering formType-1 sheets.
export const toLegacyFieldMirror = (formData = {}) => {
  const mainWorks = Array.isArray(formData.mainWorks) ? formData.mainWorks : [];
  const flatSubWorks = mainWorks.flatMap((mainWork) =>
    (Array.isArray(mainWork.subWorks) ? mainWork.subWorks : []).map((subWork) => ({
      id: subWork.id,
      subWorkName: mainWork.mainWorkName
        ? `${mainWork.mainWorkName} — ${subWork.subWorkName || ""}`.trim()
        : subWork.subWorkName || "",
      workStartDate: subWork.startDate || "",
      workExtendDate: subWork.completionDate || "",
      status: subWork.status || "Pending",
      updatedRemarks: Array.isArray(subWork.updatedRemarks)
        ? subWork.updatedRemarks
        : [],
    })),
  );
  return {
    work: mainWorks.map((mainWork) => mainWork.mainWorkName).filter(Boolean).join(", "),
    workAssignDate: mainWorks[0]?.startDate || toDateInputValue(),
    subWorks: flatSubWorks,
  };
};

export const createOtherWork = (dateValue = toDateInputValue()) => ({
  date: dateValue,
  text: "",
});

// Accepts either the current [{ date, text }] shape or a legacy plain-string
// array (from records saved before dates were attached to Other Work) and
// always returns the { date, text } shape.
export const normalizeOtherWorks = (value, fallbackDate = toDateInputValue()) => {
  const list = Array.isArray(value) ? value : [];
  return list.map((entry) =>
    typeof entry === "string"
      ? { date: fallbackDate, text: entry }
      : { date: entry?.date || fallbackDate, text: entry?.text || "" },
  );
};

export const getDefaultFormData = (formType, baseDate = new Date()) => {
  const dateValue = toDateInputValue(baseDate);
  const monthValue = toMonthKey(baseDate);

  switch (Number(formType)) {
    case 1:
      return {
        unitName: "",
        mainWorks: [createMainWork(dateValue)],
      };
    case 2:
      return {
        month: monthValue,
        metrics: createMetricSet(formType),
      };
    case 3:
      return {
        month: monthValue,
        // Old fixed daily calling metrics (Netplus / New Leads / Hotel / CP /
        // Client calling …) kept alongside the master-customer roster.
        metrics: createMetricSet(3),
        contacts: [],
        dailyStats: [],
        openingTotals: zeroDailyTotals(),
      };
    case 4:
      return {
        month: monthValue,
        // No project metric grid anymore — one Open Visits / Site Visits /
        // Phone Calls / Sales count per day, in dailyStats.
        contacts: [],
        dailyStats: [],
        openingTotals: zeroDailyTotals(),
      };
    case 5:
      return { tasks: [createAccountantTask(dateValue)] };
    case 6:
      return { tasks: [createRevenueTask(dateValue)], otherWorks: [] };
    case 7:
      return { month: monthValue, areas: createNetplusAreas() };
    case 8:
      return { month: monthValue, entries: [] };
    default:
      return {};
  }
};

export const getPeriodKey = (formType, formData, selectedDate) => {
  const type = Number(formType);
  if (type === 1) {
    // One sheet per unit — key on the normalized unit name so a later visit
    // resumes the same sheet. Falls back to the date only while no unit is set.
    const unitKey = normalizeUnitKey(formData?.unitName);
    return unitKey ? `unit:${unitKey}` : toDateInputValue(selectedDate);
  }
  if (type === 2 || type === 3 || type === 4 || type === 7 || type === 8) {
    return String(formData?.month || toMonthKey(selectedDate)).slice(0, 7);
  }
  if (type === 5 || type === 6) {
    return "ongoing";
  }
  return toDateInputValue(selectedDate);
};

export const calculateCompletionPercent = (
  formType,
  formData = {},
  status = "",
) => {
  if (/complete|completed/i.test(status || "")) return 100;

  switch (Number(formType)) {
    case 1: {
      const mainWorks = Array.isArray(formData.mainWorks)
        ? formData.mainWorks
        : [];
      const rows = mainWorks.length
        ? mainWorks.flatMap((mainWork) =>
            Array.isArray(mainWork?.subWorks) ? mainWork.subWorks : [],
          )
        : Array.isArray(formData.subWorks)
          ? formData.subWorks
          : [];
      if (!rows.length) return 20;
      const completeCount = rows.filter((row) =>
        /complete/i.test(String(row?.status || "")),
      ).length;
      return Math.round((completeCount / rows.length) * 100);
    }
    case 2:
    case 4: {
      const metrics = Array.isArray(formData.metrics) ? formData.metrics : [];
      const contacts = Array.isArray(formData.contacts)
        ? formData.contacts
        : [];
      const dailyStats = Array.isArray(formData.dailyStats)
        ? formData.dailyStats
        : [];
      let metricPct = 15;
      if (metrics.length) {
        const filledCount = metrics.filter(
          (metric) =>
            Array.isArray(metric?.entries) &&
            metric.entries.some((entry) => String(entry?.value ?? "").trim()),
        ).length;
        metricPct = Math.round((filledCount / metrics.length) * 100) || 15;
      }
      if (Number(formType) === 4 && (contacts.length || dailyStats.length)) {
        return Math.max(metricPct, 50);
      }
      return metricPct;
    }
    case 3: {
      // Blend the old fixed calling-metric fill with the new roster call rate.
      const metrics = Array.isArray(formData.metrics) ? formData.metrics : [];
      const contacts = Array.isArray(formData.contacts)
        ? formData.contacts
        : [];
      let metricPct = 0;
      if (metrics.length) {
        const filled = metrics.filter(
          (m) =>
            Array.isArray(m?.entries) &&
            m.entries.some((e) => String(e?.value ?? "").trim()),
        ).length;
        metricPct = Math.round((filled / metrics.length) * 100);
      }
      let contactPct = 0;
      if (contacts.length) {
        const called = contacts.filter(
          (c) => Array.isArray(c?.calls) && c.calls.length,
        ).length;
        contactPct = Math.round((called / contacts.length) * 100);
      }
      return Math.max(metricPct, contactPct) || 15;
    }
    case 5:
    case 6: {
      const tasks = Array.isArray(formData.tasks) ? formData.tasks : [];
      if (!tasks.length) return 20;
      const completeCount = tasks.filter((task) =>
        /done|complete|closed/i.test(String(task?.status || "")),
      ).length;
      return Math.round((completeCount / tasks.length) * 100);
    }
    case 7: {
      const areas = Array.isArray(formData.areas) ? formData.areas : [];
      if (!areas.length) return 15;
      const withData = areas.filter(
        (area) =>
          Array.isArray(area?.entries) &&
          area.entries.some((entry) =>
            NETPLUS_STATUS_FIELDS.some(
              ({ key }) => String(entry?.[key] ?? "").trim() !== "",
            ),
          ),
      ).length;
      return Math.round((withData / areas.length) * 100) || 15;
    }
    case 8: {
      const entries = Array.isArray(formData.entries) ? formData.entries : [];
      return entries.length ? 50 : 15;
    }
    default:
      return 0;
  }
};

// ─── Deadline / overdue tracking (Field Execution Journal — formType 1) ──────
// Every main work and sub work carries a `completionDate` — the planned finish
// date the sheet was set up with. These helpers answer, for the technical
// roles (Project Manager / Supervisor / Civil Engineer): has that deadline
// passed with the work still open, or was the work finished after it, and by
// how many days?

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toMidnight = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const dayGap = (later, earlier) =>
  Math.round((later.getTime() - earlier.getTime()) / MS_PER_DAY);

export const isWorkComplete = (status) =>
  /complete|completed|done/i.test(String(status || ""));

// Latest dated activity logged against a single sub-work — its newest
// updatedRemarks entry. Used as the effective "finished on" date when a row is
// marked complete but no explicit completion timestamp is stored.
const latestSubWorkActivity = (work = {}) => {
  let latest = null;
  normalizeSubWorkUpdates(work.updatedRemarks).forEach((entry) => {
    const date = toMidnight(entry.date);
    if (date && (!latest || date > latest)) latest = date;
  });
  return latest;
};

// Best-known finish date for one sub-work: its explicit completedOn stamp,
// falling back to its newest activity-log date. Null when neither exists.
const subWorkFinishDate = (sub = {}) =>
  toMidnight(sub.completedOn) || latestSubWorkActivity(sub);

// For a main work the finish date is the latest finish across its sub-works;
// for a sub-work it's its own.
const latestWorkActivity = (work = {}) => {
  if (Array.isArray(work.subWorks) && work.subWorks.length) {
    let latest = null;
    work.subWorks.forEach((sub) => {
      const date = subWorkFinishDate(sub);
      if (date && (!latest || date > latest)) latest = date;
    });
    return latest;
  }
  return subWorkFinishDate(work);
};

// Describe a single main/sub work row against its deadline. Returns null when
// the row has no deadline (completionDate) set — nothing to badge.
//
// `completionDate` is the ORIGINAL deadline and is never overwritten. An admin
// pushing the target out sets `extendedDate` instead, so `delayDays` — the
// slippage the report should surface — is always measured from the original.
// The on-track / overdue STATE is judged against the effective (extended, if
// any) deadline, since that's the date the work is actually being held to now.
//
//   state "overdue"        — still open, effective deadline is in the past
//   state "completed-late" — finished, but after the original deadline
//   state "due-soon"       — still open, effective deadline within 3 days
//   state "on-track"       — still open, effective deadline further out
//   state "on-time"        — finished on or before the original deadline
export const getWorkOverdueInfo = (work = {}, now = new Date()) => {
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  const verified = isWorkVerified(work);

  // Paused work is frozen — no overdue/due-soon reckoning while held, and no
  // effect on performance review. Resuming discards the old deadline entirely
  // (see resumeStaffReportWork), so there's nothing to compute here either.
  if (isWorkOnHold(work)) {
    return {
      state: "on-hold",
      onHold: true,
      holdReason: work.holdReason || "",
      holdDate: work.holdDate || "",
      deadline: toMidnight(work.completionDate),
      originalDeadline: toMidnight(work.completionDate),
      effectiveDeadline: toMidnight(work.completionDate),
      extendedDate: null,
      extended: false,
      extensionDays: 0,
      extensions: [],
      extensionCount: 0,
      extensionReason: "",
      extendedDueToEmployee: false,
      verified,
      wellDone: false,
      overdueDays: 0,
      daysRemaining: 0,
      delayDays: 0,
      finishedOn: null,
      label: "On hold",
    };
  }

  const originalDeadline = toMidnight(work.completionDate);
  if (!originalDeadline) return null;

  const today = toMidnight(now) || toMidnight(new Date());

  // An extension only counts when it's a real push-out past the original date.
  const extendedRaw = toMidnight(work.extendedDate);
  const extendedDate =
    extendedRaw && extendedRaw > originalDeadline ? extendedRaw : null;
  const extended = Boolean(extendedDate);
  const extensionDays = extended ? dayGap(extendedDate, originalDeadline) : 0;
  const effectiveDeadline = extendedDate || originalDeadline;

  // How many times an admin has pushed this work's deadline out, plus the
  // reason / employee-fault flag of the most recent push.
  const extensionsLog = normalizeExtensions(work.extensions);
  const extensionCount = extensionsLog.length;
  const lastExtension = extensionsLog[extensionsLog.length - 1] || null;
  const extensionReason =
    (lastExtension && lastExtension.reason) || work.extensionReason || "";
  const extendedDueToEmployee = lastExtension
    ? lastExtension.dueToEmployee
    : Boolean(work.extendedDueToEmployee);

  const base = {
    deadline: originalDeadline,
    originalDeadline,
    effectiveDeadline,
    extendedDate: extendedDate || null,
    extended,
    extensionDays,
    extensions: extensionsLog,
    extensionCount,
    extensionReason,
    extendedDueToEmployee,
    verified,
    wellDone: false,
  };

  if (isWorkComplete(work.status)) {
    // Measure lateness from the recorded finish date only. If the work is
    // marked Complete but no finish date was ever captured, do NOT assume it
    // slipped to "today" — treat it as finished on time (unknown = on time).
    const finishedOn = latestWorkActivity(work);
    const delayDays = finishedOn
      ? Math.max(0, dayGap(finishedOn, originalDeadline))
      : 0;
    if (delayDays > 0) {
      return {
        ...base,
        state: "completed-late",
        overdueDays: delayDays,
        daysRemaining: 0,
        delayDays,
        finishedOn,
        label: `Completed ${plural(delayDays)} late`,
      };
    }
    return {
      ...base,
      state: "on-time",
      overdueDays: 0,
      daysRemaining: 0,
      delayDays: 0,
      finishedOn: finishedOn || null,
      // Finished on or before the deadline AND signed off by an admin — the
      // "Well done" badge.
      wellDone: verified,
    };
  }

  const delayDays = Math.max(0, dayGap(today, originalDeadline));
  const pastEffective = dayGap(today, effectiveDeadline);

  if (pastEffective > 0) {
    return {
      ...base,
      state: "overdue",
      overdueDays: pastEffective,
      daysRemaining: 0,
      delayDays,
      finishedOn: null,
      label: extended
        ? `Extension crossed · ${plural(pastEffective)} overdue · ${plural(
            delayDays,
          )} behind`
        : `Deadline crossed · ${plural(pastEffective)} overdue`,
    };
  }

  const daysRemaining = -pastEffective;
  const timesLabel = extensionCount > 1 ? ` (${extensionCount}×)` : "";
  let label;
  if (extended) {
    label =
      delayDays > 0
        ? `Extended +${plural(extensionDays)}${timesLabel} · ${plural(
            delayDays,
          )} behind`
        : `Extended +${plural(extensionDays)}${timesLabel}`;
  } else if (daysRemaining === 0) {
    label = "Due today";
  } else if (daysRemaining === 1) {
    label = "Due tomorrow";
  } else {
    // Inclusive count: today itself is a working day, so the run is
    // today … deadline = daysRemaining + 1 (Sep 4 → Sep 6 reads "3 days").
    label = `${plural(daysRemaining + 1)} to deadline`;
  }

  return {
    ...base,
    state:
      extended && delayDays > 0
        ? "due-soon"
        : daysRemaining <= 3
          ? "due-soon"
          : "on-track",
    overdueDays: 0,
    daysRemaining,
    delayDays,
    finishedOn: null,
    label,
  };
};

// Tailwind classes for the deadline badge, keyed by getWorkOverdueInfo state.
export const workBadgeToneClass = (state) => {
  if (state === "overdue") return "bg-red-50 text-red-700 ring-1 ring-red-200";
  if (state === "completed-late" || state === "due-soon")
    return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
  if (state === "on-hold") return "bg-blue-50 text-blue-700 ring-1 ring-blue-200";
  return "bg-slate-50 text-slate-600 ring-1 ring-slate-200";
};

// True when any main or sub work in a Field Execution Journal sheet has an
// open, past-due deadline — drives the sheet-level "Overdue" badge in list
// views.
export const reportHasOverdueWork = (formData = {}, now = new Date()) => {
  const field = normalizeFieldReport(formData);
  return (Array.isArray(field.mainWorks) ? field.mainWorks : []).some((main) => {
    const rows = [main, ...(Array.isArray(main.subWorks) ? main.subWorks : [])];
    return rows.some((row) => getWorkOverdueInfo(row, now)?.state === "overdue");
  });
};

export const pickActiveReport = (reports, formType, periodKey) => {
  const list = Array.isArray(reports) ? reports : [];
  return (
    list.find(
      (report) =>
        Number(report?.formType) === Number(formType) &&
        String(report?.periodKey || "") === String(periodKey || "") &&
        !report?.isComplete,
    ) || null
  );
};

// ─── Real Estate Sales (4) — CP / Customer visitor rows ──────────────────────
// Stored server-side in their own per-employee list (not in the report's
// formData). Type and Name are required; Visit Type is optional.
export const VISITOR_TYPE_OPTIONS = ["CP", "Customer"];
export const VISIT_TYPE_OPTIONS = ["Open Visit", "Site Visit"];

export const createVisitorRow = () => ({
  id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  visitorType: "",
  visitType: "",
  name: "",
  phone: "",
  remarks: "",
});

export const isVisitorRowEmpty = (row) =>
  !["name", "phone", "remarks"].some((key) => String(row?.[key] || "").trim());
